import { SetStateAction, useEffect, useState } from 'react'

import { ScaleLinear } from 'd3'

import {
  BoxType,
  CornerPoint,
  PolyPoint,
  TailgateBox,
  TailgateGridClick,
} from '../types/types'
import { EMPTY_BOX, N_CORNERS, isValidBox } from '../utils/utils'

interface UseSetupBoxPropsIProps {
  scaleX: ScaleLinear<number, number, never>
  scaleY: ScaleLinear<number, number, never>
}

export const useSetupBox = ({ scaleX, scaleY }: UseSetupBoxPropsIProps) => {
  const [inBox, setInBox] = useState<TailgateBox>(EMPTY_BOX)
  const [outBox, setOutBox] = useState<TailgateBox>(EMPTY_BOX)

  const handleOnClick = (e: TailgateGridClick) => {
    const currentTargetRect = e.currentTarget.getBoundingClientRect()
    const position = {
      x: e.clientX - currentTargetRect.left,
      y: e.clientY - currentTargetRect.top,
    }

    if (inBox.corners.length < N_CORNERS) {
      // Draw inBox / unsecure corner
      addBoxCorner(position, inBox, BoxType.IN_BOX, setInBox)
    } else if (outBox.corners.length < N_CORNERS) {
      // Draw outBox / secure corner
      addBoxCorner(position, outBox, BoxType.OUT_BOX, setOutBox)
    }
  }

  const addBoxCorner = (
    point: PolyPoint,
    box: TailgateBox,
    boxType: BoxType,
    setBox: (value: SetStateAction<TailgateBox>) => void
  ) => {
    const newCorner: CornerPoint = {
      x: point.x,
      y: point.y,
      scaledX: scaleX(point.x),
      scaledY: scaleY(point.y),
      id: box.corners.length + 1,
      type: boxType,
    }
    setBox((b) => ({
      ...b,
      corners: [...box.corners, newCorner],
    }))
  }

  useEffect(() => {
    // Validate box on initial draw
    if (outBox?.corners?.length === 4) {
      setOutBox((box) => ({ ...box, isValid: isValidBox(box) }))
    }
    if (inBox?.corners?.length === 4) {
      setInBox((box) => ({ ...box, isValid: isValidBox(box) }))
    }
  }, [outBox?.corners, inBox?.corners])

  return {
    outBox,
    inBox,
    setOutBox,
    setInBox,
    handleOnClick,
  }
}
